import { UpdateTaskDto } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, PaginationResponse, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { TaskEntity } from '@/types/api';

import { getDashboardTaskGroupsQueryKey } from './get-dashboard-task-groups';
import { getTasksQueryKey } from './get-tasks';

export const updateTask = async (
  orgId: string,
  projectId: string,
  taskId: string,
  task: UpdateTaskDto,
): Promise<{ success: boolean }> => {
  const { data } = await api.patch<unknown, ApiResponse<{ success: boolean }>>(
    `/orgs/${orgId}/projects/${projectId}/tasks/${taskId}`,
    task,
  );

  return data;
};

export interface UseUpdateTaskOptions {
  mutationConfig?: MutationConfig<(task: UpdateTaskDto) => Promise<{ success: boolean }>>;
}

export const useUpdateTask = (taskId: string, { mutationConfig }: UseUpdateTaskOptions = {}) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId, projectId } = useScopeIds('org', 'project');

  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    mutationKey: ['tasks', 'update', taskId],
    onSuccess: (response, variables, ...args) => {
      queryClient.setQueriesData(
        { queryKey: getTasksQueryKey(orgId, projectId) },
        (old: InfiniteData<PaginationResponse<TaskEntity>>) => {
          return {
            ...old,
            pages: old.pages.map((pagination: PaginationResponse<TaskEntity>) => {
              return {
                ...pagination,
                data: pagination.data.map((task) =>
                  task.id === taskId ? { ...task, ...variables } : task,
                ),
              };
            }),
          };
        },
      );
      void queryClient.invalidateQueries({
        queryKey: getTasksQueryKey(orgId, projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: getDashboardTaskGroupsQueryKey(orgId, projectId),
      });

      notifications.success('tasks.update.success');
      onSuccess?.(response, variables, ...args);
    },
    onError: (...args) => {
      notifications.error('tasks.update.error');
      onError?.(...args);
    },
    ...restConfig,
    mutationFn: (task: UpdateTaskDto) => updateTask(orgId, projectId, taskId, task),
  });
};
